import {
  CalloutBanner,
  Divider,
  InputControl,
  Link,
  LinkUse,
  Toggle,
} from "@growtherapy/sprout-ui";
import { useLocalStorage } from "usehooks-ts";
import { deviceIsIOS } from "../../utils";
import { faClosedCaptioning } from "@fortawesome/pro-solid-svg-icons";
import { STORED_ACCESSIBILITY_SETTINGS_KEY } from "./constants";

export type AccessibilitySettings = {
  disableNotificationTimeouts: boolean;
};

export type AccessibilitySettingsTabProps = {
  onChange: (settings: AccessibilitySettings) => void;
  accessibilitySettings?: AccessibilitySettings;
  showLiveCaptioningHelp: boolean;
};

const LIVE_CAPTIONING_HELP_ARTICLE =
  "https://help.growtherapy.com/en/articles/10365766-enable-live-captioning-for-appointments";

export function AccessibilitySettingsTab({
  onChange,
  accessibilitySettings,
  showLiveCaptioningHelp,
}: AccessibilitySettingsTabProps) {
  return (
    <>
      <InputControl
        label="Disable notification timeouts"
        labelClassName="ml-2"
        //Reorder the label and toggle
        layoutClassName="flex align-center flex-wrap [&>div:first-child]:order-1 [&>div:last-child]:order-2"
        descriptionClassName="w-full"
        className="mb-0"
        description="Keep all notifications on the screen until you dismiss them."
      >
        <div className="order-0">
          <Toggle
            checked={
              accessibilitySettings?.disableNotificationTimeouts || false
            }
            onChange={(e) => {
              onChange({
                disableNotificationTimeouts: e.target.checked,
              });
            }}
          />
        </div>
      </InputControl>

      {showLiveCaptioningHelp && (
        <>
          <Divider className="mt-4 mb-6 border-neutral-600" />

          <CalloutBanner
            use="yellow"
            type="alert"
            icon={faClosedCaptioning}
            heading="Live captions available"
            headingAs="h3"
            className="bg-neutral-200"
          >
            To view captions, enable live captions in your browser or
            device&apos;s Accessibility settings.{" "}
            <Link
              href={LIVE_CAPTIONING_HELP_ARTICLE}
              use={LinkUse.Neutral}
              size="sm"
              external
            >
              Learn how to enable live captions
            </Link>
            <span className="sr-only">
              in your browser or device&apos;s Accessibility settings
            </span>
          </CalloutBanner>
        </>
      )}
    </>
  );
}

export function AccessibilitySettingsTabWrapper() {
  const [accessibilitySettings, setAccessibilitySettings] = useLocalStorage(
    STORED_ACCESSIBILITY_SETTINGS_KEY,
    {
      disableNotificationTimeouts: false,
    },
  );

  return (
    <AccessibilitySettingsTab
      onChange={setAccessibilitySettings}
      accessibilitySettings={accessibilitySettings}
      showLiveCaptioningHelp={!deviceIsIOS()}
    />
  );
}
